import React, { useEffect, useMemo, useState } from "react";
import { Table, Card, Typography, Drawer, Button, Row, Col } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Title, Text } = Typography;

// === KOMPONEN UNTUK MENAMPILKAN HISTORY SATU NOPEN ===
function DocHistory({ doc }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/monitoring-stock/history", {
          params: {
            id_customer: doc.id_customer,
            item_code: doc.item_code,
            nopen: doc.nopen,
            seri: doc.seri,
          },
        });
        setRows(res.data || []);
      } catch (e) {
        console.error("load history error", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [doc]);

  const columns = [
    {
      title: "Tanggal",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (value ? new Date(value).toLocaleString("id-ID") : ""),
      width: 180,
    },
    {
      title: "Jenis Kegiatan",
      dataIndex: "type_label",
      key: "type_label",
      width: 150,
    },
    {
      title: "No. Kegiatan",
      dataIndex: "no_kegiatan",
      key: "no_kegiatan",
      width: 140,
    },
    {
      title: "Lot",
      dataIndex: "lokasi_lot",
      key: "lokasi_lot",
      width: 80,
    },
    {
      title: "Jumlah (Kemasan)",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
      width: 140,
      align: "right",
      render: (val) => (val != null ? Number(val).toLocaleString("id-ID") : ""),
    },
    {
      title: "Catatan",
      dataIndex: "catatan",
      key: "catatan",
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={rows}
      loading={loading}
      pagination={false}
      rowKey={(r) => r.id_item_stock_history}
      size="small"
    />
  );
}

export default function MonitoringStockDoc() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  // Check user level
  const user = getUser();
  const canViewSummary =
    user &&
    user.id_level !== 8 &&
    user.id_user_level !== 8 &&
    user.id_level !== "8" &&
    user.id_user_level !== "8";
  const canViewDetail =
    user &&
    (user.id_level === 8 ||
      user.id_user_level === 8 ||
      user.id_level === "8" ||
      user.id_user_level === "8" ||
      user.id_level === 1 ||
      user.id_user_level === 1 ||
      user.id_level === "1" ||
      user.id_user_level === "1");

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const onItemClick = (item) => {
    setSelectedDoc(item);
    setDrawerVisible(true);
  };

  const loadData = async () => {
    try {
      const user = getUser();
      const params = {};

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/monitoring-stock/doc", { params });
      const fetchedData = res.data || [];
      setData(fetchedData);
    } catch (e) {
      console.error("load monitoring stock doc error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Level 3: detail per item dalam dokumen
  const renderItemTable = (docRecord, customerRecord) => {
    const columns = [
      {
        title: "Kode Barang",
        dataIndex: "item_code",
        key: "item_code",
        width: 160,
        render: (text, record) => (
          <Button
            type="link"
            style={{
              color: "#1890ff",
              cursor: "pointer",
              fontWeight: "bold",
              padding: 0,
            }}
            onClick={() => onItemClick(record)}
          >
            {text}
          </Button>
        ),
      },
      {
        title: "Nama Barang",
        dataIndex: "item_name",
        key: "item_name",
        width: 250,
      },
      {
        title: "Satuan Kemasan",
        dataIndex: "satuan_kemasan",
        key: "satuan_kemasan",
        width: 130,
      },
      {
        title: "Seri",
        dataIndex: "seri",
        key: "seri",
        width: 70,
      },
      {
        title: "Masa Timbun (hari)",
        dataIndex: "masa_timbun",
        key: "masa_timbun",
        width: 130,
      },
      {
        title: "Lokasi Lot",
        dataIndex: "lokasi_lot",
        key: "lokasi_lot",
        width: 100,
      },
      {
        title: "PCE",
        dataIndex: "total_pce",
        key: "total_pce",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
      {
        title: "CT",
        dataIndex: "total_ct",
        key: "total_ct",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
    ];

    const dataSource =
      (docRecord.items || []).map((item) => ({
        ...item,
        id_customer: item.id_customer || customerRecord.id_customer,
        nopen: item.nopen || docRecord.nopen,
        tanggal_nopen: item.tanggal_nopen || docRecord.tanggal_nopen,
      })) || [];

    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        rowKey={(row) =>
          `${row.id_customer}-${row.nopen}-${row.item_code}-${row.seri}-${row.lokasi_lot}`
        }
        size="small"
        expandable={{
          expandedRowRender: (itemRow) => <DocHistory doc={itemRow} />,
          rowExpandable: () => true,
        }}
        // === WARNA ROW BERDASARKAN MASA TIMBUN ===
        rowClassName={(record) => {
          if (record.masa_timbun > 3 * 365) return "masa-timbun-merah";
          if (record.masa_timbun > 365) return "masa-timbun-kuning";
          return "";
        }}
      />
    );
  };

// Helper to format jenis_dokumen (e.g. 16 -> BC 1.6, 27 -> BC 2.7)
const formatJenisDokumen = (val) => {
  if (!val) return "";
  const str = String(val).trim();
  if (str.toUpperCase().startsWith("BC")) return str;
  if (/^\d{2}$/.test(str)) {
    return `BC ${str.charAt(0)}.${str.charAt(1)}`;
  }
  return `BC ${str}`;
};

  // Level 2: table dokumen (nopen) per importir
  const renderDocTable = (customerRecord) => {
    const columns = [
      {
        title: "Nopen",
        dataIndex: "nopen",
        key: "nopen",
        width: 200,
        render: (text) => <b>{text}</b>,
      },
      {
        title: "Jenis Dokumen",
        dataIndex: "jenis_dokumen",
        key: "jenis_dokumen",
        width: 150,
        render: (value) => formatJenisDokumen(value),
      },
      {
        title: "Tanggal Nopen",
        dataIndex: "tanggal_nopen",
        key: "tanggal_nopen",
        width: 140,
        render: (value) => (value ? dayjs(value).format("DD-MM-YYYY") : ""),
      },
      {
        title: "PCE",
        dataIndex: "total_pce",
        key: "total_pce",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
      {
        title: "CT",
        dataIndex: "total_ct",
        key: "total_ct",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={customerRecord.docs || []}
        pagination={false}
        rowKey={(doc) =>
          `${customerRecord.id_customer}-${doc.nopen}-${doc.tanggal_nopen}`
        }
        size="small"
        expandable={{
          expandedRowRender: (docRecord) =>
            renderItemTable(docRecord, customerRecord),
          rowExpandable: (docRecord) =>
            Array.isArray(docRecord.items) && docRecord.items.length > 0,
        }}
      />
    );
  };

  // Level 1: per importir
  const columns = [
    {
      title: "Nama Importir",
      dataIndex: "nama_customer",
      key: "nama_customer",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Sum of PCE",
      dataIndex: "total_pce",
      key: "total_pce",
      width: 150,
      align: "right",
      render: (val) => (val != null ? Number(val).toLocaleString("id-ID") : ""),
    },
    {
      title: "Sum of CT",
      dataIndex: "total_ct",
      key: "total_ct",
      width: 150,
      align: "right",
      render: (val) => (val != null ? Number(val).toLocaleString("id-ID") : ""),
    },
  ];

  // Compute grand totals
  const grandTotalPCE = useMemo(() => {
    return data.reduce((sum, row) => sum + (Number(row.total_pce) || 0), 0);
  }, [data]);

  const grandTotalCT = useMemo(() => {
    return data.reduce((sum, row) => sum + (Number(row.total_ct) || 0), 0);
  }, [data]);

  // Export to Excel
  async function handleExportExcel(type) {
    let fetchedData = [];

    try {
      const user = getUser();
      const params = {};

      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username;
      }

      const res = await axiosInstance.get("/monitoring-stock/doc", {
        params,
      });
      fetchedData = res.data || [];
    } catch (e) {
      console.error("load monitoring stock doc error", e);
      return;
    }

    const wb = XLSX.utils.book_new();

    // ============ SHEET 1: DASHBOARD ============
    const dashRows = [];
    const rowOutlines = [];
    const dashMerges = [];

    const isDetail = type !== "summary";
    const titleMergeColEnd = isDetail ? 5 : 2;

    // Row 0: Title
    dashRows.push(["PT. ATLANTIK LOGISTIK BERIKAT"]);
    rowOutlines.push({});
    dashMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: titleMergeColEnd } });

    // Row 1: Date
    dashRows.push([
      `Stock Update : ${dayjs().format("DD MMMM YYYY").toUpperCase()}`,
    ]);
    rowOutlines.push({});
    dashMerges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: titleMergeColEnd } });

    // Row 2: empty
    dashRows.push([]);
    rowOutlines.push({});

    // Row 3: Headers
    if (isDetail) {
      dashRows.push([
        "IMPORTIR / NOPEN",
        "TANGGAL NOPEN",
        "KODE BARANG",
        "NAMA BARANG",
        "SUM OF PCE",
        "SUM OF CT",
      ]);
      rowOutlines.push({});
    } else {
      dashRows.push(["IMPORTIR / NOPEN", "SUM OF PCE", "SUM OF CT"]);
      rowOutlines.push({});
    }

    let currentRowIndex = 4;

    // Data rows
    fetchedData.forEach((customer) => {
      const custPce = Number(customer.total_pce) || 0;
      const custCt = Number(customer.total_ct) || 0;

      if (type === "summary" && custPce <= 0 && custCt <= 0) {
        return;
      }

      if (isDetail) {
        dashRows.push([
          customer.nama_customer || "-",
          "",
          "",
          "",
          custPce,
          custCt,
        ]);
      } else {
        dashRows.push([customer.nama_customer || "-", custPce, custCt]);
      }
      rowOutlines.push({});
      currentRowIndex++;

      if (Array.isArray(customer.docs)) {
        customer.docs.forEach((doc) => {
          const docPce = Number(doc.total_pce) || 0;
          const docCt = Number(doc.total_ct) || 0;

          if (type === "summary" && docPce <= 0 && docCt <= 0) {
            return;
          }

          if (isDetail) {
            dashRows.push([
              `  Nopen: ${doc.nopen || "-"}`,
              doc.tanggal_nopen
                ? dayjs(doc.tanggal_nopen).format("DD-MM-YYYY")
                : "-",
              "",
              "",
              docPce,
              docCt,
            ]);
            rowOutlines.push({ level: 1 });
            currentRowIndex++;

            if (Array.isArray(doc.items)) {
              doc.items.forEach((item) => {
                const itemPce = Number(item.total_pce) || 0;
                const itemCt = Number(item.total_ct) || 0;

                dashRows.push([
                  `    Seri ${item.seri || 1}`,
                  "",
                  item.item_code || "-",
                  item.item_name || "-",
                  itemPce,
                  itemCt,
                ]);
                rowOutlines.push({ level: 2 });
                currentRowIndex++;
              });
            }
          } else {
            dashRows.push([`  Nopen: ${doc.nopen || "-"}`, docPce, docCt]);
            rowOutlines.push({ level: 1 });
            currentRowIndex++;
          }
        });
      }
    });

    // Grand Total
    if (isDetail) {
      dashRows.push(["GRAND TOTAL", "", "", "", grandTotalPCE, grandTotalCT]);
    } else {
      dashRows.push(["GRAND TOTAL", grandTotalPCE, grandTotalCT]);
    }
    rowOutlines.push({});

    const wsDash = XLSX.utils.aoa_to_sheet(dashRows);

    if (isDetail) {
      wsDash["!cols"] = [
        { wch: 30 },
        { wch: 15 },
        { wch: 20 },
        { wch: 40 },
        { wch: 18 },
        { wch: 18 },
      ];
    } else {
      wsDash["!cols"] = [{ wch: 45 }, { wch: 18 }, { wch: 18 }];
    }

    wsDash["!merges"] = dashMerges;
    wsDash["!rows"] = rowOutlines;
    wsDash["!outline"] = { above: true };

    XLSX.utils.book_append_sheet(wb, wsDash, "DASHBOARD");

    // ============ INDIVIDUAL IMPORTER SHEETS ============
    const usedSheetNames = new Set(["DASHBOARD"]);

    fetchedData.forEach((customer) => {
      const custPce = Number(customer.total_pce) || 0;
      const custCt = Number(customer.total_ct) || 0;

      if (custPce <= 0 && custCt <= 0) {
        return;
      }

      const sheetRows = [];
      sheetRows.push([]);

      if (type === "summary") {
        sheetRows.push(["NOPEN", "TANGGAL NOPEN", "SUM OF PCE", "SUM OF CT"]);

        if (Array.isArray(customer.docs)) {
          customer.docs.forEach((doc) => {
            const docPce = Number(doc.total_pce) || 0;
            const docCt = Number(doc.total_ct) || 0;

            if (docPce > 0 || docCt > 0) {
              sheetRows.push([
                doc.nopen || "",
                doc.tanggal_nopen
                  ? dayjs(doc.tanggal_nopen).format("DD-MM-YYYY")
                  : "",
                docPce,
                docCt,
              ]);
            }
          });
        }
      } else {
        sheetRows.push([
          "NO",
          "NOPEN",
          "TANGGAL NOPEN",
          "SERI",
          "KODE BARANG",
          "NAMA BARANG",
          "SATUAN KEMASAN",
          "MASA TIMBUN (HARI)",
          "LOKASI LOT",
          "PCE",
          "CT",
        ]);

        let no = 1;

        if (Array.isArray(customer.docs)) {
          customer.docs.forEach((doc) => {
            if (Array.isArray(doc.items)) {
              doc.items.forEach((item) => {
                const itemPce = Number(item.total_pce) || 0;
                const itemCt = Number(item.total_ct) || 0;

                if (itemPce > 0 || itemCt > 0) {
                  sheetRows.push([
                    no++,
                    doc.nopen || "",
                    doc.tanggal_nopen
                      ? dayjs(doc.tanggal_nopen).format("DD-MM-YYYY")
                      : "",
                    item.seri || 1,
                    item.item_code || "",
                    item.item_name || "",
                    item.satuan_kemasan || "",
                    item.masa_timbun || 0,
                    item.lokasi_lot || "",
                    itemPce,
                    itemCt,
                  ]);
                }
              });
            }
          });
        }
      }

      const wsImporter = XLSX.utils.aoa_to_sheet(sheetRows);

      if (type === "summary") {
        wsImporter["!cols"] = [
          { wch: 20 },
          { wch: 15 },
          { wch: 18 },
          { wch: 18 },
        ];
      } else {
        wsImporter["!cols"] = [
          { wch: 5 },
          { wch: 20 },
          { wch: 15 },
          { wch: 8 },
          { wch: 20 },
          { wch: 40 },
          { wch: 15 },
          { wch: 18 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
        ];
      }

      let sheetName = (customer.nama_customer || "Unknown")
        .replace(/[\\\/\?\*\[\]:]/g, "")
        .trim()
        .substring(0, 31);

      let finalName = sheetName;
      let counter = 1;
      while (usedSheetNames.has(finalName.toUpperCase())) {
        finalName = `${sheetName.substring(0, 28)}_${counter}`;
        counter++;
      }
      usedSheetNames.add(finalName.toUpperCase());

      XLSX.utils.book_append_sheet(wb, wsImporter, finalName);
    });

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], { type: "application/octet-stream" });
    const fileName =
      type === "summary"
        ? `Monitoring_Stock_Doc_Summary_${dayjs().format("YYYYMMDD")}.xlsx`
        : `Monitoring_Stock_Doc_Detail_${dayjs().format("YYYYMMDD")}.xlsx`;
    saveAs(blob, fileName);
  }

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 0 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            PT. ATLANTIK LOGISTIK BERIKAT
          </Title>
          <Text type="secondary" style={{ fontSize: 14, display: "block" }}>
            Stock Update : {dayjs().format("DD MMMM YYYY").toUpperCase()}
          </Text>
        </Col>
      </Row>

      <Table
        style={{ marginTop: 16 }}
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(record) => record.id_customer}
        pagination={false}
        expandable={{
          expandedRowRender: renderDocTable,
          rowExpandable: (record) =>
            Array.isArray(record.docs) && record.docs.length > 0,
        }}
      />

      {/* GRAND TOTAL row below table */}
      {!loading && data.length > 0 && (
        <div
          style={{
            display: "flex",
            background: "#E8F4FD",
            fontWeight: 700,
            fontSize: 14,
            padding: "12px 16px",
            borderBottom: "1px solid #f0f0f0",
            borderLeft: "1px solid #f0f0f0",
            borderRight: "1px solid #f0f0f0",
          }}
        >
          <div style={{ flex: 1, paddingLeft: 48 }}>GRAND TOTAL</div>
          <div style={{ width: 150, textAlign: "right" }}>
            {grandTotalPCE.toLocaleString("id-ID")}
          </div>
          <div style={{ width: 150, textAlign: "right" }}>
            {grandTotalCT.toLocaleString("id-ID")}
          </div>
        </div>
      )}

      <Drawer
        title={`History Nopen ${selectedDoc?.nopen || ""}`}
        width={800}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        destroyOnClose
      >
        {selectedDoc && <DocHistory doc={selectedDoc} />}
      </Drawer>
    </Card>
  );
}
